import { Button, Center, Loader, Modal, Stack, Text, Tooltip } from '@mantine/core';
import { IconAlertCircle, IconFile, IconFileTypePdf, IconMaximize } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

import { api, apiOrigin } from '../api';
import { invalidatePreviewTicket, requestPreviewTicket } from '../services/preview-throttler';
import { apiErrorMessage, responseErrorMessage } from '../utils/api-error';

type FilePreviewProps = {
  fileId: string;
  name: string;
  mimeType: string | null;
  mode: 'list' | 'card';
  onOpenReview?: () => void;
};

export function shouldSuppressReport(context?: Record<string, unknown>): boolean {
  if (!context) return false;
  const status = context.status;
  if (status === 429 || status === 503) return true;
  if (typeof context.error === 'object' && context.error !== null) {
    const errorObj = context.error as Record<string, unknown>;
    if (errorObj.status === 429 || errorObj.status === 503) return true;
  }
  return false;
}

function reportClientError(message: string, context: Record<string, unknown>) {
  if (shouldSuppressReport(context)) {
    return;
  }
  console.error(`[file-preview] ${message}`, context);
  void api.api.api.v1['client-logs']
    .post({ message: `File preview: ${message}`, context })
    .catch(() => undefined);
}

async function logPreviewRenderFailure(input: {
  fileId: string;
  name: string;
  mimeType: string | null;
  source: string;
}): Promise<string> {
  try {
    const response = await fetch(input.source, { cache: 'no-store' });
    const details: Record<string, unknown> = {
      fileId: input.fileId,
      name: input.name,
      expectedMimeType: input.mimeType,
      source: input.source,
      status: response.status,
      contentType: response.headers.get('content-type'),
      contentLength: response.headers.get('content-length'),
      responseType: response.type,
    };
    if (input.mimeType === 'image/svg+xml' && response.ok) {
      const body = await response.text();
      const parsed = new DOMParser().parseFromString(body, 'image/svg+xml');
      details.receivedCharacters = body.length;
      details.rootElement = parsed.documentElement.nodeName;
      details.parserError = parsed.querySelector('parsererror')?.textContent ?? null;
    }
    reportClientError('Browser could not render preview', details);
    if (!response.ok) {
      return responseErrorMessage(response, `The preview for “${input.name}” could not be loaded.`);
    }
    const receivedType = response.headers.get('content-type');
    if (receivedType && input.mimeType && !receivedType.includes(input.mimeType)) {
      return `The preview for “${input.name}” could not be displayed because the server returned ${receivedType} instead of ${input.mimeType}.`;
    }
    return `The browser could not display the preview for “${input.name}”. The stored file may be damaged or use unsupported content.`;
  } catch (err) {
    const status =
      typeof err === 'object' && err !== null && 'status' in err && typeof err.status === 'number'
        ? err.status
        : undefined;
    reportClientError('Browser could not inspect failed preview', {
      error: err instanceof Error ? { name: err.name, message: err.message } : String(err),
      fileId: input.fileId,
      name: input.name,
      expectedMimeType: input.mimeType,
      source: input.source,
      status,
    });
    return apiErrorMessage(err, {
      fallback: `The preview for “${input.name}” could not be loaded.`,
    });
  }
}

export function FilePreview({ fileId, name, mimeType, mode, onOpenReview }: FilePreviewProps) {
  const effectiveMimeType = name.toLowerCase().endsWith('.svg') ? 'image/svg+xml' : mimeType;
  const mediaType = effectiveMimeType?.startsWith('image/')
    ? 'image'
    : effectiveMimeType?.startsWith('video/')
      ? 'video'
      : effectiveMimeType === 'application/pdf'
        ? 'pdf'
        : null;
  const isPreviewable = Boolean(mediaType && !(mediaType === 'pdf' && mode === 'list'));

  const [isVisible, setIsVisible] = useState(
    () => typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined',
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [source, setSource] = useState<string | null>(null);
  const [thumbnailSource, setThumbnailSource] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [fullPreviewOpen, setFullPreviewOpen] = useState(false);

  useEffect(() => {
    if (!isPreviewable || isVisible) return;
    const element = containerRef.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [isPreviewable, isVisible]);

  useEffect(() => {
    if (!isPreviewable || !isVisible) return;
    let active = true;
    const controller = new AbortController();
    setSource(null);
    setThumbnailSource(null);
    setFailure(null);

    const dwellTimeout = setTimeout(() => {
      void requestPreviewTicket(fileId, { signal: controller.signal })
        .then((response) => {
          if (!active) return;
          if (response.error || !response.data) {
            reportClientError('Preview ticket request failed', {
              fileId,
              name,
              mimeType: effectiveMimeType,
              status: response.status,
              error: response.error?.value,
            });
            setFailure(
              apiErrorMessage(response.error, {
                fallback: `The preview for “${name}” could not be prepared.`,
                status: response.status,
              }),
            );
            return;
          }
          setSource(`${apiOrigin}${response.data.path}`);
          setThumbnailSource(
            mediaType === 'image' && response.data.thumbnailPath
              ? `${apiOrigin}${response.data.thumbnailPath}`
              : null,
          );
        })
        .catch((err: unknown) => {
          if (!active || controller.signal.aborted) return;
          const status =
            typeof err === 'object' &&
            err !== null &&
            'status' in err &&
            typeof err.status === 'number'
              ? err.status
              : undefined;
          reportClientError('Preview ticket request threw', {
            error: err instanceof Error ? { name: err.name, message: err.message } : String(err),
            fileId,
            name,
            mimeType: effectiveMimeType,
            status,
          });
          setFailure(
            apiErrorMessage(err, {
              fallback: `The preview for “${name}” could not be prepared.`,
              status,
            }),
          );
        });
    }, 150);

    return () => {
      active = false;
      clearTimeout(dwellTimeout);
      controller.abort();
    };
  }, [effectiveMimeType, fileId, isPreviewable, isVisible, mediaType, name, retryKey]);

  function retry() {
    invalidatePreviewTicket(fileId);
    setRetryKey((current) => current + 1);
  }

  function handleRenderFailure() {
    if (!source) return;
    invalidatePreviewTicket(fileId);
    setFailure(`The preview for “${name}” could not be displayed. Checking the server response…`);
    void logPreviewRenderFailure({
      fileId,
      name,
      mimeType: effectiveMimeType,
      source,
    }).then(setFailure);
  }

  function handleImageRenderFailure() {
    if (thumbnailSource) {
      reportClientError('Thumbnail could not render; using original preview', {
        fileId,
        name,
        mimeType: effectiveMimeType,
        source: thumbnailSource,
      });
      setThumbnailSource(null);
      return;
    }
    handleRenderFailure();
  }

  if (!mediaType) {
    return (
      <Center className={`file-preview file-preview-${mode} file-preview-fallback`}>
        <IconFile size={mode === 'card' ? 48 : 24} stroke={1.4} />
      </Center>
    );
  }
  if (failure) {
    if (mode === 'list') {
      return (
        <Tooltip label={failure} multiline w={320}>
          <button
            type='button'
            className='file-preview file-preview-list file-preview-fallback file-preview-error-button'
            aria-label={`${failure} Retry preview.`}
            onClick={retry}
          >
            <IconAlertCircle size={25} stroke={1.6} />
          </button>
        </Tooltip>
      );
    }
    return (
      <Stack
        className='file-preview file-preview-card file-preview-fallback'
        align='center'
        justify='center'
        gap='xs'
        p='md'
      >
        <IconAlertCircle size={30} stroke={1.5} />
        <Text size='xs' c='red' ta='center' lineClamp={3}>
          {failure}
        </Text>
        <Button size='compact-xs' variant='default' onClick={retry}>
          Retry preview
        </Button>
      </Stack>
    );
  }
  if (mediaType === 'pdf' && mode === 'list') {
    return (
      <Center className='file-preview file-preview-list file-preview-pdf'>
        <IconFileTypePdf size={27} stroke={1.5} />
      </Center>
    );
  }
  if (!source) {
    return (
      <Center ref={containerRef} className={`file-preview file-preview-${mode}`}>
        <Loader size={mode === 'card' ? 'sm' : 'xs'} />
      </Center>
    );
  }
  if (mediaType === 'image') {
    return (
      <>
        <button
          type='button'
          className={`file-preview file-preview-${mode} file-preview-button${
            effectiveMimeType === 'image/svg+xml' ? ' file-preview-svg' : ''
          }`}
          aria-label={
            onOpenReview ? `Open ${name} in media review` : `Open full preview of ${name}`
          }
          title={onOpenReview ? 'Open media review' : 'Open full preview'}
          onClick={(event) => {
            event.stopPropagation();
            if (onOpenReview) onOpenReview();
            else setFullPreviewOpen(true);
          }}
        >
          <img
            src={thumbnailSource ?? source}
            alt={name}
            loading='lazy'
            onError={handleImageRenderFailure}
          />
        </button>
        <Modal
          opened={fullPreviewOpen}
          onClose={() => setFullPreviewOpen(false)}
          title={name}
          fullScreen
          classNames={{ body: 'file-preview-modal-body' }}
        >
          <div className='file-preview-modal-canvas'>
            <img src={source} alt={name} onError={handleRenderFailure} />
          </div>
        </Modal>
      </>
    );
  }
  if (mediaType === 'pdf') {
    return (
      <div className='file-preview file-preview-card file-preview-pdf'>
        <iframe
          src={`${source}#toolbar=0&navpanes=0`}
          title={`PDF preview for ${name}`}
          onError={handleRenderFailure}
        />
      </div>
    );
  }
  return (
    <div className={`file-preview file-preview-${mode}`}>
      <video
        src={source}
        aria-label={`Video preview for ${name}`}
        controls={mode === 'card'}
        muted={mode === 'list'}
        playsInline
        preload='metadata'
        onError={handleRenderFailure}
      />
      {onOpenReview && (
        <button
          type='button'
          className='file-preview-review-button'
          aria-label={`Open ${name} in media review`}
          title='Open media review'
          onClick={(event) => {
            event.stopPropagation();
            onOpenReview();
          }}
        >
          <IconMaximize size={mode === 'card' ? 18 : 15} />
        </button>
      )}
    </div>
  );
}
